'use client';

import { c as _c } from "react/compiler-runtime";
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import { useQuery } from '@tanstack/react-query';
import { ArrowUpRight, Search as SearchIcon, Sparkles } from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { decodeScope, scopeInput, scopeKey, type SearchScope } from '../../hooks/useSearchScope';
import AppLayout from '../../layouts/AppLayout/AppLayout';
import { client } from '../../utils/trpc';
import SearchResultRow from '../common/SearchResultRow';
import ResearchCoverage from './ResearchCoverage';
import ResearchPanel from './ResearchPanel';
const SUGGESTIONS = ['people building climate tech', 'spaces about design', 'posts explaining trons', 'who writes about AI'];
const IdleState = t0 => {
  const $ = _c(7);
  const {
    onPick
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="mx-auto mb-5 flex size-16 items-center justify-center rounded-3xl bg-gradient-to-br from-primary/15 to-primary/5 text-primary ring-1 ring-primary/15"><Sparkles className="size-7" strokeWidth={1.75} /></div>;
    t2 = <h2 className="text-xl font-semibold text-textPrimary">Find anything, in plain words</h2>;
    t3 = <p className="mx-auto mt-2 text-sm leading-relaxed text-textSecondary">Search people, spaces, factiiis, and posts.</p>;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] !== onPick) {
    t4 = SUGGESTIONS.map(s => <button key={s} onClick={() => onPick(s)} className="rounded-full border border-ds-border-standard bg-background-secondary px-3.5 py-2 text-[13px] text-textSecondary transition-all hover:border-primary hover:text-textPrimary">{s}</button>);
    $[3] = onPick;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t4) {
    t5 = <div className="mx-auto max-w-sm px-6 py-20 text-center">{t1}{t2}{t3}<div className="mt-7 flex flex-wrap justify-center gap-2">{t4}</div></div>;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
};

// Always-present research entry point at the top of /search. With a query it
// offers to deepen that search; empty (search box untouched) it invites the
// user to research any topic — the panel then prompts them for one.
const ResearchCTA = t0 => {
  const $ = _c(14);
  const {
    query,
    onStart
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-indigo-500 text-white shadow-sm"><Sparkles className="size-5" /></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== query) {
    t2 = query ? <>Research “<span className="text-primary">{query}</span>” with AI</> : "Research anything with AI";
    $[1] = query;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <div className="text-[15px] font-emphasis leading-snug text-textPrimary">{t2}</div>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = query ? "Claude searches the web and posts a summary so this returns more." : "Ask Claude to research a topic and post what it finds.";
  let t5;
  if ($[5] !== t4) {
    t5 = <p className="mt-0.5 truncate text-[13px] text-textSecondary">{t4}</p>;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t3 || $[8] !== t5) {
    t6 = <div className="min-w-0 flex-1">{t3}{t5}</div>;
    $[7] = t3;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ArrowUpRight className="size-5 shrink-0 text-textSecondary opacity-0 transition-all group-hover:opacity-100" />;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== onStart || $[12] !== t6) {
    t8 = <button onClick={onStart} className="group mb-4 flex w-full items-center gap-3 rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 to-primary/5 px-4 py-3 text-left transition-all hover:border-primary/40 hover:from-primary/15">{t1}{t6}{t7}</button>;
    $[11] = onStart;
    $[12] = t6;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
};
const UnifiedResults = t0 => {
  const $ = _c(31);
  const {
    query,
    scope
  } = t0;
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  let t1;
  let t2;
  if ($[0] !== query) {
    t1 = () => {
      const handler = setTimeout(() => setDebouncedQuery(query), 400);
      return () => clearTimeout(handler);
    };
    t2 = [query];
    $[0] = query;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== scope) {
    t3 = scope ? scopeKey(scope) : "";
    $[3] = scope;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== debouncedQuery || $[6] !== t3) {
    t4 = ["semanticSearch", debouncedQuery, t3];
    $[5] = debouncedQuery;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== debouncedQuery || $[9] !== scope) {
    t5 = () => unwrapFromResult(client.search.semantic.query({
      query: debouncedQuery,
      ...(scope ? {
        scope: scopeInput(scope)
      } : {})
    }));
    $[8] = debouncedQuery;
    $[9] = scope;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = debouncedQuery.length > 0;
  let t7;
  if ($[11] !== t4 || $[12] !== t5 || $[13] !== t6) {
    t7 = {
      queryKey: t4,
      queryFn: t5,
      enabled: t6,
      placeholderData: _temp
    };
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const {
    data,
    isLoading,
    isError
  } = useQuery(t7);
  if (isLoading) {
    return null;
  }
  if (isError) {
    let t8;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <p className="px-3 py-16 text-center text-sm text-destructive">Something went wrong. Try again.</p>;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    return t8;
  }
  let t8;
  if ($[16] !== data?.results) {
    t8 = data?.results ?? [];
    $[16] = data?.results;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const results = t8;
  if (results.length === 0) {
    let t9;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <div className="flex size-12 items-center justify-center rounded-2xl bg-background-secondary text-textSecondary"><SearchIcon className="size-6" strokeWidth={1.5} /></div>;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    let t10;
    if ($[19] !== debouncedQuery) {
      t10 = <span className="font-emphasis text-textPrimary">{debouncedQuery}</span>;
      $[19] = debouncedQuery;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    let t11;
    if ($[21] !== scope) {
      t11 = scope && <>{" "}in{" "}<span className="font-emphasis text-textPrimary">{scope.label}</span></>;
      $[21] = scope;
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    const t12 = scope ? "Close the pill in the search bar to search everywhere." : "Try researching it with AI above.";
    let t13;
    if ($[23] !== t10 || $[24] !== t11 || $[25] !== t12) {
      t13 = <div className="flex flex-col items-center gap-4 px-6 py-20 text-center">{t9}<p className="text-sm text-textSecondary">Nothing matched “{t10}”{t11}.{" "}{t12}</p></div>;
      $[23] = t10;
      $[24] = t11;
      $[25] = t12;
      $[26] = t13;
    } else {
      t13 = $[26];
    }
    return t13;
  }
  let t9;
  if ($[27] !== results) {
    t9 = results.map(_temp2);
    $[27] = results;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== t9) {
    t10 = <div className="space-y-1">{t9}</div>;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  return t10;
};

// The query is driven by the header search bar (it owns the input and writes
// ?q and ?scope); this page just reads them and renders the results. Keeping
// the scope in the URL is what lets a scoped search be shared or reloaded —
// the bar reads the same param back to draw its pill.
const Search = () => {
  const $ = _c(29);
  const router = useRouter();
  const queryParams = useSearchParams();
  let t0;
  if ($[0] !== queryParams) {
    t0 = (queryParams?.get("q") || "").trim();
    $[0] = queryParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const query = t0;
  let t1;
  if ($[2] !== queryParams) {
    t1 = decodeScope(queryParams?.get("scope") ?? null);
    $[2] = queryParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const scope = t1;
  const [researching, setResearching] = useState(false);
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      queryKey: ["poolStatus"],
      queryFn: _temp3
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: pool
  } = useQuery(t2);
  const poolHasTrons = pool != null && pool.mode !== "OFF" && pool.balance > 0 && pool.myRemaining4h > 0 && pool.poolRemaining4h > 0;
  let t3;
  if ($[5] !== queryParams || $[6] !== router) {
    t3 = value => {
      const params = new URLSearchParams(queryParams?.toString() || "");
      if (value) {
        params.set("q", value);
      } else {
        params.delete("q");
      }
      router.push(`/search?${params.toString()}`, {
        scroll: false
      });
    };
    $[5] = queryParams;
    $[6] = router;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const goToQuery = t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setResearching(true);
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== query) {
    t5 = <ResearchCTA query={query} onStart={t4} />;
    $[9] = query;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== poolHasTrons) {
    t6 = <div className="-mt-2 mb-4 px-1"><ResearchCoverage poolHasTrons={poolHasTrons} /></div>;
    $[11] = poolHasTrons;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== query || $[14] !== scope) {
    t7 = query && <p className="mb-3 px-1 text-[13px] text-textSecondary">Results for{" "}<span className="font-emphasis text-textPrimary">“{query}”</span>{scope && <>{" "}in{" "}<span className="font-emphasis text-primary">{scope.label}</span></>}</p>;
    $[13] = query;
    $[14] = scope;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== goToQuery || $[17] !== query || $[18] !== scope) {
    t8 = query ? <UnifiedResults query={query} scope={scope} /> : <IdleState onPick={goToQuery} />;
    $[16] = goToQuery;
    $[17] = query;
    $[18] = scope;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== query || $[21] !== researching) {
    t9 = researching && <ResearchPanel query={query} closeHandler={() => setResearching(false)} />;
    $[20] = query;
    $[21] = researching;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t5 || $[24] !== t6 || $[25] !== t7 || $[26] !== t8 || $[27] !== t9) {
    t10 = <AppLayout showPostRenderOptions={false}><div className="mx-auto mt-6 w-full max-w-2xl px-4 pb-28">{t5}{t6}{t7}{t8}{t9}</div></AppLayout>;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  return t10;
};
export default Search;
function _temp(previousData) {
  return previousData;
}
function _temp2(result, i) {
  return <SearchResultRow key={`${result.type}-${i}`} result={result} index={i} />;
}
function _temp3() {
  return unwrapFromResult(client.pool.status.query());
}